/**
 * Campaign Tags Service
 *
 * Handles all API calls related to campaign tags:
 * - CRUD operations
 * - Tag reordering
 */

import api, { getCsrfCookie } from './api';
import type {
  CampaignTag,
  CampaignTagsResponse,
  CampaignTagResponse,
  CreateCampaignTagData,
  UpdateCampaignTagData,
} from '../types/campaign';

/**
 * Fetch all tags for the authenticated user's account
 */
export async function getTags(): Promise<CampaignTag[]> {
  const response = await api.get<CampaignTagsResponse>('/api/campaign-tags');
  return response.data.tags;
}

/**
 * Create a new tag
 */
export async function createTag(data: CreateCampaignTagData): Promise<CampaignTag> {
  await getCsrfCookie();
  const response = await api.post<CampaignTagResponse>('/api/campaign-tags', data);
  return response.data.tag;
}

/**
 * Update an existing tag
 */
export async function updateTag(
  id: number,
  data: UpdateCampaignTagData
): Promise<CampaignTag> {
  await getCsrfCookie();
  const response = await api.put<CampaignTagResponse>(`/api/campaign-tags/${id}`, data);
  return response.data.tag;
}

/**
 * Delete a tag
 */
export async function deleteTag(id: number): Promise<void> {
  await getCsrfCookie();
  await api.delete(`/api/campaign-tags/${id}`);
}

/**
 * Reorder tags
 */
export async function reorderTags(tagIds: number[]): Promise<void> {
  await getCsrfCookie();
  await api.put('/api/campaign-tags/reorder', { tag_ids: tagIds });
}

export const campaignTagService = {
  getTags,
  createTag,
  updateTag,
  deleteTag,
  reorderTags,
};
